import { ApiError, type CheckoutApi, type InvoiceStreamHandlers } from "./types";
import type {
  BillingPayResponse,
  DemoInvoiceRequest,
  PublicBillingInvoice,
  PublicInvoice,
  PublicPaymentLink,
  PublicRefundClaim,
  PublicSubscription,
  PublicTopUp,
  SignupInfo,
  SignupResponse,
  SolanaPrep,
} from "../lib/types";

const enc = encodeURIComponent;

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, "")}${path}`;
}

async function request<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(joinUrl(base, path), {
      ...init,
      headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}), ...init?.headers },
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError("network", "Could not reach the payment server.");
  }
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    const e = (body as { error?: { code?: string; message?: string } } | null)?.error;
    const code = e?.code ?? (res.status === 404 ? "not_found" : `http_${res.status}`);
    throw new ApiError(code, e?.message ?? res.statusText, res.status);
  }
  return body as T;
}

/**
 * The stream's message shape is not pinned down in the contract beyond "status changes", so accept
 * the reasonable variants: a full invoice, `{ invoice }`, `{ data: { invoice } }` (WebhookEvent-like)
 * or a bare `{ status, txHash? }` patch.
 */
export function parseStreamMessage(data: string): Partial<PublicInvoice> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const o = parsed as Record<string, unknown>;
  const nested = (o.data as Record<string, unknown> | undefined)?.invoice ?? o.invoice;
  const candidate = (nested && typeof nested === "object" ? nested : o) as Partial<PublicInvoice>;
  return typeof candidate.status === "string" ? candidate : null;
}

export function createHttpApi(baseUrl: string): CheckoutApi {
  return {
    getInvoice(id, signal) {
      return request<PublicInvoice>(baseUrl, `/public/invoices/${encodeURIComponent(id)}`, { signal });
    },

    getPublicConfig(signal) {
      return request<unknown>(baseUrl, `/public/config`, { signal });
    },

    subscribe(id: string, handlers: InvoiceStreamHandlers) {
      if (typeof EventSource === "undefined") {
        handlers.onError(true);
        return () => {};
      }
      const source = new EventSource(joinUrl(baseUrl, `/public/invoices/${encodeURIComponent(id)}/events`));
      const onMessage = (ev: MessageEvent<string>) => {
        const patch = parseStreamMessage(ev.data);
        if (patch) handlers.onEvent(patch);
      };
      source.onopen = () => handlers.onOpen();
      source.onmessage = onMessage;
      // Servers may name events after the status or the webhook type; listen to the likely names.
      for (const name of ["status", "invoice", "update"]) source.addEventListener(name, onMessage as EventListener);
      // After a network drop EventSource retries on its own (readyState CONNECTING). After an HTTP
      // error (404, 503 "too many streams", …) it stops for good (CLOSED) and the caller reopens it.
      source.onerror = () => handlers.onError(source.readyState === EventSource.CLOSED);
      return () => source.close();
    },

    async hint(id, txHash) {
      await request<unknown>(baseUrl, `/public/invoices/${encodeURIComponent(id)}/hint`, {
        method: "POST",
        body: JSON.stringify({ txHash }),
      });
    },

    solanaPrep(id) {
      return request<SolanaPrep>(baseUrl, `/public/solana/prep/${encodeURIComponent(id)}`);
    },

    createDemoInvoice(body: DemoInvoiceRequest) {
      return request<{ id: string }>(baseUrl, `/demo/invoices`, { method: "POST", body: JSON.stringify(body) });
    },

    getPaymentLink(handle, slug, signal) {
      return request<PublicPaymentLink>(baseUrl, `/public/links/${enc(handle)}/${enc(slug)}`, { signal });
    },
    payPaymentLink(handle, slug, body) {
      return request<BillingPayResponse>(baseUrl, `/public/links/${enc(handle)}/${enc(slug)}/pay`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    getBillingInvoice(token, signal) {
      return request<PublicBillingInvoice>(baseUrl, `/public/billing/${enc(token)}`, { signal });
    },
    payBillingInvoice(token) {
      return request<BillingPayResponse>(baseUrl, `/public/billing/${enc(token)}/pay`, { method: "POST", body: "{}" });
    },
    billingPdfUrl(pdfPath, print) {
      // pdfPath comes from the server ("/public/billing/<token>/pdf"); only that shape is accepted.
      const safe = /^\/public\/billing\/[A-Za-z0-9]+\/pdf$/.test(pdfPath) ? pdfPath : "/public/billing/invalid/pdf";
      return joinUrl(baseUrl || window.location.origin, `${safe}${print ? "?format=print" : ""}`);
    },
    getSubscription(token, signal) {
      return request<PublicSubscription>(baseUrl, `/public/subscriptions/${enc(token)}`, { signal });
    },
    cancelSubscription(token) {
      return request<PublicSubscription>(baseUrl, `/public/subscriptions/${enc(token)}/cancel`, { method: "POST", body: "{}" });
    },
    getRefundClaim(token, signal) {
      return request<PublicRefundClaim>(baseUrl, `/public/refunds/${enc(token)}`, { signal });
    },
    claimRefund(token, body) {
      return request<PublicRefundClaim>(baseUrl, `/public/refunds/${enc(token)}`, { method: "POST", body: JSON.stringify(body) });
    },
    getTopUp(id, signal) {
      return request<PublicTopUp>(baseUrl, `/public/topups/${enc(id)}`, { signal });
    },
    getSignupInfo(signal) {
      return request<SignupInfo>(baseUrl, `/public/signup`, { signal });
    },
    signup(body) {
      return request<SignupResponse>(baseUrl, `/public/signup`, { method: "POST", body: JSON.stringify(body) });
    },
  };
}
